import React, { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import '../hd-styles.css';

const API_URL = '/api';

function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [allianceLeaderboard, setAllianceLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contributors');
  const [userRank, setUserRank] = useState(null);

  const accountHash = localStorage.getItem('avalw_account_hash');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contribRes, allianceRes] = await Promise.all([
          fetch(`${API_URL}/leaderboard?limit=50`),
          fetch(`${API_URL}/leaderboard/alliances?limit=50`)
        ]);

        const contribData = await contribRes.json();
        const allianceData = await allianceRes.json();

        if (contribData.success) {
          setLeaderboard(contribData.leaderboard || []);
          if (accountHash) {
            const userEntry = contribData.leaderboard.find(u => u.full_hash === accountHash);
            if (userEntry) {
              setUserRank(userEntry.rank);
            }
          }
        }

        if (allianceData.success) {
          setAllianceLeaderboard(allianceData.leaderboard || []);
        }
      } catch (err) {
        console.error('Failed to fetch leaderboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [accountHash]);

  const formatNum = (num) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return Math.round(num).toString();
  };

  const getBadgeClass = (badge) => {
    if (!badge) return 'starter';
    const name = badge.name?.toLowerCase() || '';
    if (name.includes('legend')) return 'legendary';
    if (name.includes('diamond')) return 'diamond';
    if (name.includes('gold')) return 'gold';
    if (name.includes('silver')) return 'silver';
    if (name.includes('bronze')) return 'bronze';
    return 'starter';
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="page-loading">
          <div className="spinner"></div>
          <p>Loading Top 50...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="page-content leaderboard-page">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-left">
            <h1>Leaderboard</h1>
            <p className="page-subtitle">Top 50 Contributors - Ranked by Contribution Score</p>
          </div>
          {userRank && (
            <div className="your-rank-badge">
              <span className="rank-label">Your Rank</span>
              <span className="rank-value">#{userRank}</span>
            </div>
          )}
        </div>

        {/* Score Formula Card */}
        <div className="formula-card">
          <div className="formula-title">How Contribution Score Works</div>
          <div className="formula-equation">
            <span className="formula-part">
              <strong>Uptime Hours</strong> x 10
            </span>
            <span className="formula-op">+</span>
            <span className="formula-part">
              <strong>Core-Hours</strong>
            </span>
            <span className="formula-op">+</span>
            <span className="formula-part">
              <strong>Cache Served</strong> / 1000
            </span>
          </div>
          <p className="formula-note">Consistency beats raw power - stay online longer to climb!</p>
        </div>

        {/* Tabs */}
        <div className="leaderboard-tabs">
          <button
            className={`lb-tab-btn ${activeTab === 'contributors' ? 'active' : ''}`}
            onClick={() => setActiveTab('contributors')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            Contributors
          </button>
          <button
            className={`lb-tab-btn ${activeTab === 'alliances' ? 'active' : ''}`}
            onClick={() => setActiveTab('alliances')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
            Alliances
          </button>
        </div>

        {/* Contributors Table */}
        {activeTab === 'contributors' && (
          <div className="leaderboard-table-wrap">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th className="th-rank">Rank</th>
                  <th className="th-user">Contributor</th>
                  <th className="th-badge">Badge</th>
                  <th className="th-score">Score</th>
                  <th className="th-uptime">Uptime</th>
                  <th className="th-cores">Core-Hrs</th>
                  <th className="th-cache">Cache</th>
                  <th className="th-status">Status</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((user) => (
                  <tr
                    key={user.id}
                    className={`lb-row ${user.full_hash === accountHash ? 'is-you' : ''} ${user.rank <= 3 ? `top-${user.rank}` : ''}`}
                  >
                    <td className="td-rank">
                      {user.rank <= 3 ? (
                        <span className={`rank-medal rank-${user.rank}`}>
                          {user.rank === 1 && (
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                              <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z"/>
                            </svg>
                          )}
                          {user.rank}
                        </span>
                      ) : (
                        <span className="rank-num">#{user.rank}</span>
                      )}
                    </td>
                    <td className="td-user">
                      <div className="user-cell">
                        <div
                          className="user-avatar-small"
                          style={{ backgroundColor: user.badge?.color || '#6366f1' }}
                        >
                          {user.display_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="user-info">
                          <span className="user-name">{user.display_name}</span>
                          <span className="user-id">{user.id}</span>
                        </div>
                      </div>
                    </td>
                    <td className="td-badge">
                      <span className={`badge-pill ${getBadgeClass(user.badge)}`}>
                        {user.badge?.name || 'Starter'}
                      </span>
                    </td>
                    <td className="td-score">
                      <span className="score-value">{formatNum(user.contribution_score)}</span>
                    </td>
                    <td className="td-uptime">{user.uptime_days || 0}d</td>
                    <td className="td-cores">{formatNum(user.core_hours_total)}</td>
                    <td className="td-cache">{formatNum(user.total_cache_served)}</td>
                    <td className="td-status">
                      <span className={`status-pill ${user.worker_status}`}>
                        <span className="status-dot"></span>
                        {user.worker_status === 'online' ? 'Online' : 'Offline'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Alliances Table */}
        {activeTab === 'alliances' && (
          <div className="leaderboard-table-wrap">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th className="th-rank">Rank</th>
                  <th className="th-user">Alliance</th>
                  <th className="th-badge">Level</th>
                  <th className="th-score">CO2 Saved</th>
                  <th className="th-members">Members</th>
                  <th className="th-online">Online</th>
                </tr>
              </thead>
              <tbody>
                {allianceLeaderboard.map((alliance) => (
                  <tr
                    key={alliance.id}
                    className={`lb-row ${alliance.rank <= 3 ? `top-${alliance.rank}` : ''}`}
                  >
                    <td className="td-rank">
                      {alliance.rank <= 3 ? (
                        <span className={`rank-medal rank-${alliance.rank}`}>
                          {alliance.rank === 1 && (
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                              <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z"/>
                            </svg>
                          )}
                          {alliance.rank}
                        </span>
                      ) : (
                        <span className="rank-num">#{alliance.rank}</span>
                      )}
                    </td>
                    <td className="td-user">
                      <div className="user-cell">
                        <div
                          className="user-avatar-small alliance-avatar"
                          style={{ backgroundColor: alliance.banner_color || '#8b5cf6' }}
                        >
                          {alliance.name?.charAt(0)?.toUpperCase() || 'A'}
                        </div>
                        <div className="user-info">
                          <span className="user-name">{alliance.name}</span>
                          <span className="user-id">{alliance.description?.substring(0, 30) || ''}</span>
                        </div>
                      </div>
                    </td>
                    <td className="td-badge">
                      <span className="level-badge">Lv.{alliance.level}</span>
                    </td>
                    <td className="td-score">
                      <span className="score-value eco">{alliance.co2_saved_kg} kg</span>
                    </td>
                    <td className="td-members">{alliance.member_count}</td>
                    <td className="td-online">
                      <span className="online-count">
                        <span className="status-dot online"></span>
                        {alliance.online_count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default Leaderboard;
